import type { ReactNode } from "react";

import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type { Obligatoriedad } from "@/lib/estructuraDePlantilla";
import {
  ETIQUETA_DE_OBLIGATORIEDAD,
  etiquetaDeTipo,
  type TipoDeContenido,
} from "@/lib/plantillas";
import { cn } from "@/lib/utils";

/* Los tres niveles de obligatoriedad y su color, en un solo lugar: el listado,
   el árbol, el cajón y el maestro tienen que leerse igual. Verde = obliga,
   ámbar = depende, gris = a gusto. */
export const ESTILO_DE_OBLIGATORIEDAD: Record<Obligatoriedad, { pildora: string; punto: string }> =
  {
    obligatorio: {
      pildora: "bg-success-soft text-success-soft-foreground",
      punto: "bg-success-dot",
    },
    condicional: {
      pildora: "bg-warning-soft text-warning-soft-foreground",
      punto: "bg-warning-dot",
    },
    opcional: { pildora: "bg-muted text-ink-muted", punto: "bg-ink-hint" },
  };

const ESTILO_DE_TIPO: Record<TipoDeContenido, string> = {
  texto: "bg-muted text-ink-muted",
  cuadro_de_valor: "bg-info-soft text-info-soft-foreground",
  tabla: "bg-tipo-tabla-soft text-tipo-tabla-soft-foreground",
  imagen: "bg-tipo-imagen-soft text-tipo-imagen-soft-foreground",
  imagen_mapa: "bg-tipo-mapa-soft text-tipo-mapa-soft-foreground",
  firma: "bg-tipo-firma-soft text-tipo-firma-soft-foreground",
  documento_anexo: "bg-tipo-anexo-soft text-tipo-anexo-soft-foreground",
};

export function ChipDeTipo({
  tipo,
  etiqueta,
  ancho,
  className,
}: {
  tipo: TipoDeContenido;
  etiqueta?: string;
  /** Ancho fijo y texto centrado: en una lista los chips forman una columna. */
  ancho?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex w-fit items-center rounded-[0.375rem] px-2 py-0.5 text-[0.6875rem] font-bold whitespace-nowrap",
        ancho && "w-[7.375rem] shrink-0 justify-center truncate px-[0.4375rem]",
        ESTILO_DE_TIPO[tipo],
        className,
      )}
    >
      {etiqueta ?? etiquetaDeTipo(tipo)}
    </span>
  );
}

/** Punto de color de una obligatoriedad. */
export function PuntoDeObligatoriedad({
  obligatoriedad,
  className,
}: {
  obligatoriedad: Obligatoriedad | "";
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-block size-2 shrink-0 rounded-full",
        obligatoriedad ? ESTILO_DE_OBLIGATORIEDAD[obligatoriedad].punto : "bg-border",
        className,
      )}
    />
  );
}

interface PildoraProps {
  obligatoriedad: Obligatoriedad | "";
  /** Con `onClick` la píldora es un botón (cicla el valor). */
  onClick?: () => void;
  titulo?: string;
  className?: string;
}

export function PildoraDeObligatoriedad({
  obligatoriedad,
  onClick,
  titulo,
  className,
}: PildoraProps) {
  const etiqueta = obligatoriedad ? ETIQUETA_DE_OBLIGATORIEDAD[obligatoriedad] : "No incluido";
  const clases = cn(
    "inline-flex items-center justify-center gap-1.5 rounded-full px-2.5 py-1 text-[0.71875rem] font-bold whitespace-nowrap",
    obligatoriedad ? ESTILO_DE_OBLIGATORIEDAD[obligatoriedad].pildora : "text-ink-hint bg-muted/60",
    className,
  );
  const contenido = (
    <>
      <PuntoDeObligatoriedad obligatoriedad={obligatoriedad} />
      {etiqueta}
    </>
  );

  if (!onClick) return <span className={clases}>{contenido}</span>;

  return (
    <button
      type="button"
      onClick={onClick}
      title={titulo}
      className={cn(
        clases,
        "focus-visible:ring-ring/50 cursor-pointer outline-none focus-visible:ring-[3px]",
      )}
    >
      {contenido}
    </button>
  );
}

/** Barra de composición O / C / P de una plantilla. */
export function BarraDeComposicion({
  obligatorios,
  condicionales,
  opcionales,
}: {
  obligatorios: number;
  condicionales: number;
  opcionales: number;
}) {
  const total = obligatorios + condicionales + opcionales || 1;
  return (
    <div
      className="bg-muted/60 flex h-1.5 w-full gap-[2px] overflow-hidden rounded-full"
      role="img"
      aria-label={`${obligatorios} obligatorios, ${condicionales} condicionales, ${opcionales} opcionales`}
    >
      <span className="bg-success-dot" style={{ width: `${(obligatorios / total) * 100}%` }} />
      <span className="bg-warning-dot" style={{ width: `${(condicionales / total) * 100}%` }} />
      <span className="bg-ink-ghost" style={{ width: `${(opcionales / total) * 100}%` }} />
    </div>
  );
}

/** Los tres conteos O / C / P con su punto, para filas de grupo y listado. */
export function ConteoDeObligatoriedad({
  obligatorios,
  condicionales,
  opcionales,
}: {
  obligatorios: number;
  condicionales: number;
  opcionales: number;
}) {
  const celdas: [Obligatoriedad, number, string][] = [
    ["obligatorio", obligatorios, "obligatorios"],
    ["condicional", condicionales, "condicionales"],
    ["opcional", opcionales, "opcionales"],
  ];
  return (
    <span className="text-ink-muted flex items-center gap-2.5 text-[0.6875rem] font-semibold">
      {celdas.map(([ob, n, nombre]) => (
        <span key={ob} className="flex items-center gap-1" title={`${n} ${nombre}`}>
          <PuntoDeObligatoriedad obligatoriedad={ob} className="size-1.5" />
          <span className="font-mono">{n}</span>
        </span>
      ))}
    </span>
  );
}

export interface OpcionSegmentada<T extends string> {
  valor: T;
  etiqueta: ReactNode;
  /** Clases del botón cuando está activo. */
  activo?: string;
  /** Nombre accesible si `etiqueta` no es texto. */
  nombre?: string;
  /** Clases propias de la opción (tamaño de letra, relleno). */
  className?: string;
}

/**
 * Control segmentado (una opción activa de varias): contenedor gris con la
 * opción activa en blanco y una sombra suave. Radix permite deseleccionar un
 * `ToggleGroup` single con otro clic; acá una opción siempre está elegida, así
 * que el valor vacío se ignora.
 *
 * `lleno` reparte el ancho en partes iguales (el cajón); sin él cada opción
 * mide lo que su texto.
 */
export function Segmentado<T extends string>({
  valor,
  opciones,
  onChange,
  etiqueta,
  deshabilitado,
  lleno,
  className,
}: {
  valor: T;
  opciones: OpcionSegmentada<T>[];
  onChange: (valor: T) => void;
  etiqueta: string;
  deshabilitado?: boolean;
  lleno?: boolean;
  className?: string;
}) {
  return (
    <ToggleGroup
      type="single"
      size="sm"
      spacing={1}
      value={valor}
      disabled={deshabilitado}
      aria-label={etiqueta}
      onValueChange={(siguiente) => {
        if (siguiente) onChange(siguiente as T);
      }}
      style={lleno ? { gridTemplateColumns: `repeat(${opciones.length}, minmax(0, 1fr))` } : undefined}
      className={cn(
        "bg-muted/60 gap-[3px] rounded-[0.6875rem] p-[3px]",
        lleno ? "grid w-full" : "w-fit",
        className,
      )}
    >
      {opciones.map((opcion) => (
        <ToggleGroupItem
          key={opcion.valor}
          value={opcion.valor}
          aria-label={opcion.nombre}
          className={cn(
            "text-ink-muted data-[state=on]:bg-background data-[state=on]:text-ink h-[2.125rem] rounded-lg px-3 text-xs font-bold hover:bg-transparent data-[state=on]:shadow-sm",
            opcion.activo,
            opcion.className,
          )}
        >
          {opcion.etiqueta}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

/** Barra de presencia: negra sobre pista gris, con "N de M" al lado. */
export function BarraDePresencia({
  valor,
  total,
  className,
}: {
  valor: number;
  total: number;
  className?: string;
}) {
  const proporcion = total > 0 ? Math.min(1, valor / total) : 0;
  return (
    <div className={cn("flex items-center gap-2.5", className)}>
      <div
        className="bg-muted/60 h-1.5 flex-1 overflow-hidden rounded-full"
        role="img"
        aria-label={`Presente en ${valor} de ${total} informes`}
      >
        <div className="bg-foreground h-full rounded-full" style={{ width: `${proporcion * 100}%` }} />
      </div>
      <span className="text-ink-body text-xs font-semibold whitespace-nowrap">
        <span className="text-ink font-mono font-bold">{valor}</span> de {total}
      </span>
    </div>
  );
}

/** Rótulo en versalitas de una sección del cajón o de un cuadro. */
export function RotuloDeSeccion({
  children,
  tono = "ink",
  className,
}: {
  children: ReactNode;
  tono?: "ink" | "hint";
  className?: string;
}) {
  return (
    <span
      className={cn(
        "text-[0.6875rem] font-extrabold tracking-[0.04em] uppercase",
        tono === "ink" ? "text-ink" : "text-ink-hint",
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Opciones O / C / P para un `Segmentado` de obligatoriedad. */
export const OPCIONES_DE_OBLIGATORIEDAD: OpcionSegmentada<Obligatoriedad>[] = [
  {
    valor: "obligatorio",
    nombre: "Obligatorio",
    etiqueta: (
      <span className="flex items-center gap-1.5">
        <PuntoDeObligatoriedad obligatoriedad="obligatorio" />
        Obligatorio
      </span>
    ),
    activo: "data-[state=on]:text-success-soft-foreground",
  },
  {
    valor: "condicional",
    nombre: "Condicional",
    etiqueta: (
      <span className="flex items-center gap-1.5">
        <PuntoDeObligatoriedad obligatoriedad="condicional" />
        Condicional
      </span>
    ),
    activo: "data-[state=on]:text-warning-soft-foreground",
  },
  {
    valor: "opcional",
    nombre: "Opcional",
    etiqueta: (
      <span className="flex items-center gap-1.5">
        <PuntoDeObligatoriedad obligatoriedad="opcional" />
        Opcional
      </span>
    ),
  },
];
